// dependencies
import { ArcElement, Chart as ChartJS, DoughnutController } from "chart.js";
import React, { useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { useResizeObserver } from "usehooks-ts";

//components
import SmallChip from "../../../../components/Chip/templates/SmallChip";
import { Divider } from "../../../../components/Divider";
import FormattedDateTime from "../../../../components/FormattedDateTime";
import MarkdownReader from "../../../../components/MarkdownReader";
import Typography from "../../../../components/Text";

//services
import { Goal } from "../../../../api/services";

import { getColor } from "../../../../utils/getColor";
//utils
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_VALUE_COLOR, formatStatus } from "./utils";

//css
import css from "./GoalCard.module.scss";

// Register Chart.js elements
ChartJS.register(ArcElement, DoughnutController);

type GoalCardProps = {
	goal: Goal;
};

const MAX_LINES = 20;

const GoalCard: React.FC<GoalCardProps> = ({ goal }) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const { height: descriptionHeight = 0 } = useResizeObserver<HTMLDivElement>({
		ref: ref as React.RefObject<HTMLDivElement>,
	});

	const { value, target, color, name, end_date, goal_weight, status, categories, last_update } = goal;

	const formattedStatus = formatStatus(status || "");

	let percentComplete = value / target;
	if (percentComplete > 1) {
		percentComplete = 1;
	} else if (percentComplete < 0) {
		percentComplete = 0;
	}

	return (
		<div className={css.goalCardContainer} id="goal-card" data-testid="goal-card">
			<div className={css.goalCardHeader}>
				<div className={css.goalCardChart}>
					<Doughnut
						key={`goal-chart-${goal._id || Math.random()}`}
						width={42}
						options={{
							animation: false,
							events: [],
							plugins: {
								tooltip: {
									enabled: false,
								},
								legend: {
									display: false,
								},
							},
						}}
						data={{
							datasets: [
								{
									data: [percentComplete, 1 - percentComplete],
									backgroundColor: [color?.color || DEFAULT_VALUE_COLOR, DEFAULT_BACKGROUND_COLOR],
									borderWidth: 0,
								},
							],
						}}
					/>
				</div>
				<div>
					<Typography size="paragraph-3" weight="semi-bold" className={css.goalName}>
						{name}
					</Typography>
					<SmallChip
						label={`Status: ${formattedStatus}`}
						color="neutral"
						isActive
						innerStyle={{ backgroundColor: getColor("neutral", 7) }}
					/>
				</div>
			</div>
			<div>
				{goal?.description ? (
					<div
						className={`${css.goalCardDescriptionWrapper} ${
							descriptionHeight > MAX_LINES * 16 ? css.goalCardDescriptionWrapperOverflow : null
						}`}
					>
						<div ref={ref}>
							<MarkdownReader markdownText={goal?.description} className={css.goalCardDescription} />
						</div>
					</div>
				) : (
					<Divider />
				)}
			</div>
			<div className={css.goalCardDetails}>
				{end_date ? (
					<div className={css.dataRow}>
						<Typography
							className={css.goalCardSectionTitle}
							element="span"
							size="caption"
							weight="semi-bold"
						>
							Due Date:
						</Typography>
						<FormattedDateTime
							dateSize="medium"
							hasTime={false}
							date={end_date}
							element="span"
							size="caption"
							weight="regular"
						/>
					</div>
				) : null}

				{last_update ? (
					<div className={css.dataRow}>
						<Typography
							className={css.goalCardSectionTitle}
							element="span"
							size="caption"
							weight="semi-bold"
						>
							Last Update:
						</Typography>
						<FormattedDateTime
							dateSize="medium"
							hasTime={false}
							date={last_update}
							element="span"
							size="caption"
							weight="regular"
						/>
					</div>
				) : null}

				{goal_weight !== null && goal_weight !== undefined ? (
					<div className={css.dataRow}>
						<Typography
							className={css.goalCardSectionTitle}
							element="span"
							size="caption"
							weight="semi-bold"
						>
							Goal Weight:
						</Typography>
						<Typography className={css.goalCardSectionTitle} element="span" size="caption" weight="regular">
							{goal_weight}
						</Typography>
					</div>
				) : null}
				{categories && categories.length > 0 ? (
					<div className={css.dataRow}>
						<div className={css.chipGroup}>
							<Typography element="span" size="caption" weight="semi-bold">
								Categories:
							</Typography>
							{categories.map((category) => (
								<SmallChip
									labelProps={{ size: "caption", className: "!whitespace-normal" }}
									innerStyle={{ maxWidth: "100%", whiteSpace: "normal" }}
									key={category._id}
									label={
										category.name.length > 75 ? category.name.slice(0, 75) + "..." : category.name
									}
									color="neutral"
								/>
							))}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default GoalCard;
